import {
  property,
  state,
  html,
  customElement,
  css,
  cssManager,
  type TemplateResult,
  type CSSResult,
} from '@design.estate/dees-element';
import { DeesThumbnailBase } from '../dees-thumbnail-shared/DeesThumbnailBase.js';
import { thumbnailBaseStyles } from '../dees-thumbnail-shared/styles.js';
import { demo } from './demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-thumbnail-audio': DeesThumbnailAudio;
  }
}

@customElement('dees-thumbnail-audio')
export class DeesThumbnailAudio extends DeesThumbnailBase {
  public static demo = demo;
  public static demoGroups = ['Media'];
  public static styles = [
    ...thumbnailBaseStyles,
    css`
      .audio-content {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        background: var(--dees-color-bg-secondary);
      }

      .music-icon {
        font-size: 32px;
        color: var(--dees-color-text-secondary);
        opacity: 0.8;
      }

      .audio-title {
        font-size: 12px;
        font-weight: 600;
        color: var(--dees-color-text-primary);
        text-align: center;
        padding: 0 16px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .audio-artist {
        font-size: 11px;
        font-weight: 400;
        color: var(--dees-color-text-secondary);
        text-align: center;
        padding: 0 16px;
        margin-top: -12px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .waveform-container {
        width: calc(100% - 32px);
        height: 40px;
        position: relative;
        overflow: hidden;
      }

      .waveform-container canvas {
        width: 100%;
        height: 100%;
        display: block;
      }


    `,
  ] as any;

  @property({ type: String })
  accessor src: string = '';

  @property({ type: String })
  accessor title: string = '';

  @property({ type: String })
  accessor artist: string = '';

  @state()
  accessor duration: number = 0;

  @state()
  accessor waveformData: number[] = [];

  @state()
  accessor waveformReady: boolean = false;

  @state()
  accessor isPreviewPlaying: boolean = false;

  private loadController?: AbortController;
  private hasLoadedWaveform = false;

  protected renderTileContent(): TemplateResult {
    return html`
      <div class="audio-content">
        <dees-icon class="music-icon" icon="lucide:Music"></dees-icon>

        ${this.title ? html`<div class="audio-title">${this.title}</div>` : ''}
        ${this.artist ? html`<div class="audio-artist">${this.artist}</div>` : ''}

        ${this.waveformReady ? html`
          <div class="waveform-container">
            <canvas></canvas>
          </div>
        ` : ''}
      </div>

    `;
  }

  protected renderBottomBar(): TemplateResult | string {
    if (!this.label && !this.duration) return '';
    return html`
      <div class="tile-info-bar">
        ${this.label ? html`<span class="info-label" title="${this.label}">${this.label}</span>` : ''}
        <span class="info-spacer"></span>
        ${this.duration > 0 ? html`<span class="info-detail">${this.formatTime(this.duration)}</span>` : ''}
      </div>
    `;
  }

  protected getTileClickDetail(): Record<string, unknown> {
    return {
      src: this.src,
      title: this.title,
      artist: this.artist,
      duration: this.duration,
    };
  }

  protected onBecameVisible(): void {
    if (!this.hasLoadedWaveform && this.src) {
      this.hasLoadedWaveform = true;
      this.loadAudioMeta();
    }
  }

  private async loadAudioMeta(): Promise<void> {
    this.loadController?.abort();
    const controller = new AbortController();
    this.loadController = controller;
    this.loading = true;
    this.error = false;
    let context: AudioContext | undefined;
    try {
      const response = await fetch(this.src, { signal: controller.signal });
      if (!response.ok) throw new Error(`Audio request failed: ${response.status}`);
      const bytes = await response.arrayBuffer();
      if (controller.signal.aborted) return;
      context = new AudioContext();
      const buffer = await context.decodeAudioData(bytes);
      if (controller.signal.aborted || !this.isConnected) return;
      this.duration = buffer.duration;
      const channel = buffer.getChannelData(0);
      const count = Math.min(80, channel.length);
      const waveform = Array.from({ length: count }, (_, index) => {
        const start = Math.floor(index * channel.length / count);
        const end = Math.floor((index + 1) * channel.length / count);
        let sum = 0;
        for (let i = start; i < end; i++) sum += Math.abs(channel[i]);
        return sum / (end - start);
      });
      const max = Math.max(0, ...waveform);
      this.waveformData = waveform.map(value => max ? value / max : 0);
      this.waveformReady = true;
    } catch {
      if (!controller.signal.aborted) this.error = true;
    } finally {
      if (context) await context.close();
      if (this.loadController === controller) {
        this.loadController = undefined;
        this.loading = false;
      }
    }
  }

  private drawWaveform(): void {
    if (!this.waveformReady) return;

    const canvas = this.shadowRoot?.querySelector('.waveform-container canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const container = canvas.parentElement!;
    const dpr = window.devicePixelRatio || 1;
    const width = container.clientWidth;
    const height = container.clientHeight;

    canvas.width = width * dpr;
    canvas.height = height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const bars = this.waveformData.length;
    if (bars === 0) return;

    const barWidth = width / bars;

    ctx.fillStyle = getComputedStyle(this).getPropertyValue('--dees-color-text-secondary').trim();
    for (let i = 0; i < bars; i++) {
      const amplitude = this.waveformData[i];
      const barHeight = Math.max(2, amplitude * (height - 4));
      const x = i * barWidth;
      const y = (height - barHeight) / 2;
      ctx.fillRect(x + 0.5, y, barWidth - 1, barHeight);
    }
  }

  protected willUpdate(changed: Map<PropertyKey, unknown>): void {
    super.willUpdate(changed);
    if (changed.has('src')) {
      this.loadController?.abort();
      this.loadController = undefined;
      this.hasLoadedWaveform = false;
      this.waveformReady = false;
      this.waveformData = [];
      this.duration = 0;
      this.loading = false;
      this.error = false;
      if (this.isVisible && this.src) this.onBecameVisible();
    }
  }

  public async updated(changedProperties: Map<PropertyKey, unknown>): Promise<void> {
    super.updated(changedProperties);
    if (this.waveformReady) this.drawWaveform();
  }

  public async disconnectedCallback(): Promise<void> {
    this.loadController?.abort();
    this.loadController = undefined;
    this.hasLoadedWaveform = false;
    this.loading = false;
    await super.disconnectedCallback();
  }

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
